import { Button, Input, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gem,
  ScanLine,
  Search,
  RotateCcw,
  Hash,
  Truck,
  Calendar,
  User,
  MapPin,
  Package,
  Tag,
  Ruler,
  Palette,
  Percent,
  Boxes,
  Wallet,
  ShoppingBag,
  Loader2,
  Sparkles,
} from "lucide-react";
import { CREATE_jwel } from "../../Config/Config";

/* ---------- design tokens ---------- */
const palette = {
  ink: "#1F2430",
  muted: "#6B7280",
  bg: "#F4F5FA",
  card: "#FFFFFF",
  border: "#E7E8F0",
  brand: "#111827",
  gold: "#B8862F",
  goldTint: "#FBF3E3",
  sapphire: "#2F5FE0",
  sapphireTint: "#EAF0FE",
  emerald: "#1C9A6C",
  emeraldTint: "#E7F7EF",
  amethyst: "#7A3FE0",
  amethystTint: "#F1EAFD",
  ruby: "#D8264F",
  rubyTint: "#FCE9EE",
};

const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap');
`;

/* ---------- decorative backdrop: soft orbits & glow shapes ---------- */
const BackgroundDecor = () => {
  const floaters = [
    { top: "12%", left: "6%", size: 10, color: palette.gold, duration: 6 },
    { top: "70%", left: "9%", size: 7, color: palette.sapphire, duration: 7.5 },
    { top: "22%", left: "92%", size: 8, color: palette.ruby, duration: 5.5 },
    { top: "82%", left: "88%", size: 9, color: palette.emerald, duration: 6.8 },
    { top: "48%", left: "3%", size: 6, color: palette.amethyst, duration: 8 },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", zIndex: 0, pointerEvents: "none" }}>
      {/* soft color glows */}
      <div
        style={{
          position: "absolute",
          top: -120,
          left: -100,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: palette.sapphire,
          opacity: 0.14,
          filter: "blur(90px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 160,
          right: -140,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: palette.gold,
          opacity: 0.16,
          filter: "blur(100px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -160,
          left: "28%",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background: palette.emerald,
          opacity: 0.13,
          filter: "blur(100px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 20,
          right: "8%",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: palette.ruby,
          opacity: 0.11,
          filter: "blur(90px)",
        }}
      />

      {/* rotating orbit rings, echoing a cut gemstone's facets */}
      <motion.svg
        width="560"
        height="560"
        viewBox="0 0 560 560"
        style={{ position: "absolute", top: -140, right: -160, opacity: 0.5 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 70, ease: "linear" }}
      >
        <circle cx="280" cy="280" r="250" fill="none" stroke={palette.gold} strokeWidth="1" strokeDasharray="2 12" />
        <circle cx="280" cy="280" r="185" fill="none" stroke={palette.sapphire} strokeWidth="1" strokeDasharray="1 9" />
      </motion.svg>

      <motion.svg
        width="420"
        height="420"
        viewBox="0 0 420 420"
        style={{ position: "absolute", bottom: -120, left: -120, opacity: 0.45 }}
        animate={{ rotate: -360 }}
        transition={{ repeat: Infinity, duration: 90, ease: "linear" }}
      >
        <circle cx="210" cy="210" r="180" fill="none" stroke={palette.emerald} strokeWidth="1" strokeDasharray="1 11" />
        <circle cx="210" cy="210" r="125" fill="none" stroke={palette.ruby} strokeWidth="1" strokeDasharray="2 9" />
      </motion.svg>

      {/* small floating gem specks */}
      {floaters.map((f, i) => (
        <motion.span
          key={i}
          style={{
            position: "absolute",
            top: f.top,
            left: f.left,
            width: f.size,
            height: f.size,
            background: f.color,
            opacity: 0.35,
            borderRadius: 3,
            transform: "rotate(45deg)",
          }}
          animate={{ y: [0, -14, 0] }}
          transition={{ repeat: Infinity, duration: f.duration, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

/* ---------- motion variants ---------- */
const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

/* ---------- small building blocks ---------- */
const Field = ({ icon: Icon, label, value, accent }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 4px",
      borderBottom: `1px solid ${palette.border}`,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: palette.muted }}>
      {Icon && <Icon size={15} strokeWidth={2} color={accent} />}
      <span style={{ fontSize: 13.5, fontFamily: "Inter, sans-serif", fontWeight: 500 }}>
        {label}
      </span>
    </div>
    <span
      style={{
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        color: palette.ink,
        textAlign: "right",
      }}
    >
      {value || "\u2013"}
    </span>
  </div>
);

const Section = ({ icon: Icon, title, accent, tint, children }) => (
  <motion.div
    variants={itemVariants}
    style={{
      background: palette.card,
      borderRadius: 14,
      border: `1px solid ${palette.border}`,
      overflow: "hidden",
      marginBottom: 16,
      boxShadow: "0 1px 2px rgba(17,24,39,0.04)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 18px",
        background: tint,
        borderBottom: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} color="#fff" strokeWidth={2.2} />
      </div>
      <span
        style={{
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          fontSize: 14.5,
          color: palette.ink,
          letterSpacing: 0.1,
        }}
      >
        {title}
      </span>
    </div>
    <div style={{ padding: "6px 18px 10px" }}>{children}</div>
  </motion.div>
);

const BarCodeCheck = () => {
  const [barCode, setBarCode] = useState("");
  const [barCodeData, setBarCodeData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [noStock, setNoStock] = useState(false);

  const tenantName = localStorage.getItem("tenantName");
  const adminType = localStorage.getItem("adminType");

  /* Returns true when the design code has at least one row with stock.
     Rows with Qty 0 are never shown; if only Qty 0 rows exist a single
     toast is shown instead. */
  const barCodeAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CURRENT_STOCK&where=DesignCode='${barCode}'`,
        { headers: { tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        // const inStock = data.filter((item) => Number(item?.Qty || 0) > 0);
        // if (inStock.length === 0) {
        //   setNoStock(true);
        //   message.warning("This item has no stock (Qty 0)");
        //   return false;
        // }
        setBarCodeData(data);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching estimation count:", error);
      return false;
    }
  };

  const branchAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=BRANCH_MASTER`,
        { headers: { tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setBranchData(data);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const stockDetailsAPI = async (cloudDbName, branchName) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetStockByBranch?BranchName=${cloudDbName}&DesignCode=${barCode}`,
        { headers: { tenantName } },
      );
      const data = Array.isArray(response.data) ? response.data : [];
      if (data.length) {
        const updated = data.map((item) => ({ ...item, STORENAME: branchName }));
        setStockData((prev) => [...prev, ...updated]);
      }
    } catch (error) {
      console.error(`Error fetching stock for ${branchName}:`, error);
    }
  };

  const handleShow = async () => {
    if (!barCode) return;
    setLoading(true);
    // clear the previous result first so old details never linger
    setBarCodeData([]);
    setStockData([]);
    setNoStock(false);
    try {
      const hasStock = await barCodeAPI();
      // no stock for this design code → skip the branch stock calls too
      if (hasStock) {
        for (const branch of branchData) {
          await stockDetailsAPI(branch.CLOUD_DBNAME, branch.Branchname);
        }
      }
      setSearched(true);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBarCode("");
    setBarCodeData([]);
    setStockData([]);
    setSearched(false);
    setNoStock(false);
  };

  useEffect(() => {
    branchAPI();
  }, []);

  const hasData = barCodeData.length > 0;

  return (
    <div
      style={{
        position: "relative",
        background: "linear-gradient(180deg, #F6F4FF 0%, #F4F5FA 45%, #FBF6EC 100%)",
        minHeight: "100%",
        padding: "28px 20px",
        overflow: "hidden",
      }}
    >
      <style>{fontImport}</style>
      <BackgroundDecor />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: palette.brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Gem size={18} color={palette.gold} strokeWidth={2} />
          </div>
          <div>
            <h3
              style={{
                margin: 0,
                fontFamily: "Poppins, sans-serif",
                fontWeight: 700,
                fontSize: 19,
                color: palette.ink,
              }}
            >
              Barcode Check
            </h3>
            <p style={{ margin: 0, fontSize: 12.5, color: palette.muted, fontFamily: "Inter, sans-serif" }}>
              Scan or enter a design code to pull product, stock and pricing details
            </p>
          </div>
        </div>

        {/* search bar */}
        <div
          style={{
            background: palette.goldTint,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: "16px 18px",
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 240px" }}>
            <ScanLine
              size={17}
              color={palette.gold}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <Input
              value={barCode}
              autoFocus
              placeholder="Enter design code"
              onChange={(e) => setBarCode(e.target.value)}
              onPressEnter={handleShow}
              style={{
                paddingLeft: 34,
                height: 40,
                borderRadius: 10,
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <motion.div whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }}>
            <Button
              type="primary"
              onClick={handleShow}
              disabled={loading}
              style={{
                height: 40,
                borderRadius: 10,
                background: palette.brand,
                borderColor: palette.brand,
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
              }}
            >
              {loading ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
                  style={{ display: "flex" }}
                >
                  <Loader2 size={15} />
                </motion.span>
              ) : (
                <Search size={15} />
              )}
              Show
            </Button>
          </motion.div>

          <motion.div whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }}>
            <Button
              danger
              type="primary"
              onClick={handleReset}
              style={{
                height: 40,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: palette.ruby,
                borderColor: palette.ruby,
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
              }}
            >
              <RotateCcw size={15} />
              Reset
            </Button>
          </motion.div>
        </div>

        {/* design code banner — only once a match with stock is found */}
        <AnimatePresence>
          {hasData && (
            <motion.div
              key="banner"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: palette.brand,
                color: "#fff",
                borderRadius: 10,
                padding: "10px 16px",
                marginBottom: 18,
                width: "fit-content",
              }}
            >
              <Hash size={15} color={palette.gold} />
              <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: 14 }}>
                {barCodeData[0]?.DesignCode}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {searched && !hasData && (
          <div
            style={{
              textAlign: "center",
              color: palette.muted,
              padding: "10px 0 18px",
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
            }}
          >
            {noStock
              ? "No stock available for that design code — showing empty details below."
              : "No product found for that design code — showing empty details below."}
          </div>
        )}

        {/* section cards are always visible, with placeholders until a code is searched */}
        <motion.div variants={listVariants} initial="hidden" animate="visible">
          {adminType === "ADMIN" && (
            <Section icon={Truck} title="Supplier Information" accent={palette.sapphire} tint={palette.sapphireTint}>
              <Field icon={Hash} label="Inv No" value={barCodeData[0]?.InvNO} accent={palette.sapphire} />
              <Field
                icon={Calendar}
                label="Inv Date"
                value={barCodeData[0]?.Invdate ? dayjs(barCodeData[0]?.Invdate).format("DD-MMM-YYYY") : null}
                accent={palette.sapphire}
              />
              <Field icon={Hash} label="Entry No" value={barCodeData[0]?.ENTRYNO} accent={palette.sapphire} />
              <Field
                icon={Calendar}
                label="Entry Date"
                value={barCodeData[0]?.ENTRYDATE ? dayjs(barCodeData[0]?.ENTRYDATE).format("DD-MMM-YYYY") : null}
                accent={palette.sapphire}
              />
              <Field icon={User} label="Party Name" value={barCodeData[0]?.Partyname} accent={palette.sapphire} />
              <Field icon={MapPin} label="City" value={barCodeData[0]?.Area} accent={palette.sapphire} />
            </Section>
          )}

          <Section icon={Package} title="Product Information" accent={palette.emerald} tint={palette.emeraldTint}>
            <Field icon={Tag} label="Purchase Type" value={barCodeData[0]?.PTYPE} accent={palette.emerald} />
            <Field icon={Package} label="Product Name" value={barCodeData[0]?.ProdName} accent={palette.emerald} />
            <Field icon={Tag} label="Brand Name" value={barCodeData[0]?.CompName} accent={palette.emerald} />
            <Field icon={Tag} label="Group Name" value={barCodeData[0]?.GroupName} accent={palette.emerald} />
            <Field icon={Ruler} label="Size" value={barCodeData[0]?.Prodsize} accent={palette.emerald} />
            <Field icon={Sparkles} label="Fashion" value={barCodeData[0]?.FITTING} accent={palette.emerald} />
            <Field icon={Palette} label="Color" value={barCodeData[0]?.COLOUR} accent={palette.emerald} />
            <Field icon={Percent} label="Offer" value={barCodeData[0]?.SLEVES} accent={palette.emerald} />
            <Field icon={Hash} label="HSN Code" value={barCodeData[0]?.HSNCODE} accent={palette.emerald} />
            <Field icon={Hash} label="Code" value={barCodeData[0]?.Code} accent={palette.emerald} />
          </Section>

          <Section icon={Boxes} title="Stock" accent={palette.amethyst} tint={palette.amethystTint}>
            <Field icon={Boxes} label="Qty" value={barCodeData[0]?.Qty} accent={palette.amethyst} />
            {stockData.map((branch, index) => (
              <Field
                key={index}
                icon={MapPin}
                label={`${branch?.STORENAME} Qty`}
                value={branch?.Qty}
                accent={palette.amethyst}
              />
            ))}
          </Section>

          {adminType === "ADMIN" && (
            <Section icon={Wallet} title="Purchase Cost Details" accent={palette.gold} tint={palette.goldTint}>
              <Field
                icon={Wallet}
                label="Gross DP"
                value={barCodeData[0]?.GrosDPrice ? Number(barCodeData[0]?.GrosDPrice).toFixed(2) : "0.00"}
                accent={palette.gold}
              />
              <Field
                icon={Percent}
                label="GST(%)"
                value={`${barCodeData[0]?.GSTNumber ? barCodeData[0]?.GST : 0}%`}
                accent={palette.gold}
              />
              <Field
                icon={Wallet}
                label="Net DP"
                value={barCodeData[0]?.DPrice ? Number(barCodeData[0]?.DPrice).toFixed(2) : "0.00"}
                accent={palette.gold}
              />
              <Field
                icon={Wallet}
                label="Whole Sale Price"
                value={barCodeData[0]?.WPRICE ? Number(barCodeData[0]?.WPRICE).toFixed(2) : "0.00"}
                accent={palette.gold}
              />
            </Section>
          )}

          <Section icon={ShoppingBag} title="Sale Cost Details" accent={palette.ruby} tint={palette.rubyTint}>
            <Field
              icon={ShoppingBag}
              label="MRP"
              value={barCodeData[0]?.CPrice ? Number(barCodeData[0]?.CPrice).toFixed(2) : "0.00"}
              accent={palette.ruby}
            />
            <Field
              icon={Percent}
              label="Dis(%)"
              value={`${barCodeData[0]?.Disamt ? Number(barCodeData[0]?.Disamt) : 0}%`}
              accent={palette.ruby}
            />
            <Field icon={Tag} label="OFFER Price" value={barCodeData[0]?.OFFER_DIS} accent={palette.ruby} />
          </Section>
        </motion.div>
      </div>
    </div>
  );
};

export default BarCodeCheck;